//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import OrganizationStaff from './OrganizationStaffs';

//Component Begin

export default function Capacity() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
  } = useFormContext();

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        const updatedStep5 = { ...getValues('step5') };

        res.forEach((r, resIndex) => {
          const { sign } = r.value;
          const currentPayload = payloads[resIndex];
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          const relevantFiles = currentPayload.files.filter((file) =>
            currentPayload.signAssetPayload.some((payload) => payload.fileName === file.name)
          );

          updatedStep5[currentPayload.field.key] = {
            ...updatedStep5[currentPayload.field.key],
            selectedFiles: signRes.map((sig, index) => ({
              name: relevantFiles[index].name,
              path: sig.path,
              key: sig.key,
              type: relevantFiles[index].type,
              field: currentPayload.field.key,
              description: currentPayload.field.description,
            })),
          };
        });

        updateData({
          step5: updatedStep5,
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step5 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step5)[0];
      if (firstErrorKey) setFocus(`step5.${firstErrorKey}`);
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step5');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles && selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step5[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);
        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();

    if (!isValid) {
      return;
    }
    const formData = getValues('step5');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step5: {
          ...formData,
          staffs: [...formContextData.step5.staffs],
        },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Capacity</h3>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm text-neutralColor-medium col-span-6'>
            Any organization receiving federal funds must have financial management systems that meet these key requirements, as defined in
            24 CFR 84.21, which includes the following areas:
          </h3>
          <ul className='list-disc ml-8 my-2'>
            <li>Accurate, complete and current Records</li>
            <li>Internal Controls</li>
            <li>Budget Control</li>
            <li>Accounting record</li>
            <li>Allowable Cost Control</li>
            <li>Cash management</li>
            <li>Audit Requirements</li>
          </ul>

          <HookFileInput name='step5.f1.selectedFiles' label='Upload evidence of compliance:' id='selectedFiles' multiple />
          {/* <HookFileInput
            name="step5.f2.selectedFiles"
            label="Certification from a CPA"
            id="selectedFiles"
            multiple
          />
          <HookFileInput
            name="step5.f3.selectedFiles"
            label="HUD approved audit summary"
            id="selectedFiles"
            multiple
          /> */}
        </div>
        <HookInput customClassName='col-span-2' label='Identify fiscal year' name='step5.fiscalYear' type='date' />
        {/* <div className="col-span-6">
          <HookRadioGroup
            label="Are you in compliance with all DOH or CHFA agreements?"
            name="step5.hasCompliance"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
          />
        </div> */}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f4.selectedFiles'
            label='Please provide evidence that you have demonstrated capacity and experience to act as a developer/ owner/ sponsor and carry out the  proposed development activity to be assisted with HOME funds'
            id='selectedFiles'
            multiple
          />
        </div>
        <div className='col-span-6'>
          <OrganizationStaff />
        </div>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm text-neutralColor-medium col-span-6 mb-2'>
            CHDOs must have paid staff with experience and capacity in affordable housing development. Staff must be knowledgeable in
            project financing, development, and management. The use of consultants or volunteers alone does not meet the capacity
            requirement.
          </h3>
          <HookFileInput
            name='step5.f5.selectedFiles'
            label='Submit evidence of staff capacity(i.e. - resumes,
            certificates, degrees)'
            id='selectedFiles'
            multiple
          />
        </div>
        <div className='col-span-6'>
          <p>
            The CHDO or its parent organization must be able to show one year of serving the community prior to the date the Participating
            Jurisdiction provides HOME funds to the organization. The organization must describe its history (or its parent organization's
            history) of serving the community by submitting a narrative describing activities which it provided (or its parent organization
            provided), such as developing new housing, rehabilitating existing stock and managing housing stock, or delivering non-housing
            services that have had lasting benefits for the community, such as counseling, food relief, or childcare facilities. The
            narrative must demonstrate that the CHDO has a history of serving the community within which housing to be assisted with HOME
            funds is to be located, as evidenced by
          </p>
          <div className='space-y-5 mt-2'>
            <HookFileInput name='step5.f6.selectedFiles' id='selectedFiles' multiple />
            <HookTextarea id='description' name='step5.f6.description' label='Description' srOnlyLabel rows={3} />
          </div>
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
